import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useWallet } from "@/context/WalletContext";
import { supabase } from "@/lib/supabase";
import {
    isOnchainAvailable,
    readRefund,
    withdrawRefundOnchain,
    readClient,
    fromWei,
    toExplorerTx,
} from "@/lib/bidzoneAuction";
import {
    isNftAvailable,
    readNftRefund,
    withdrawNftRefundOnchain,
} from "@/lib/nft";

/**
 * Auto-refund scanner.
 *
 * Problem it solves (reported Nov 2026): when bidder A is outbid by bidder B,
 * the BidzoneAuction contract credits A's refund slot via the pull-model
 * `refundOf[auctionId][A]`. The contract CANNOT push A's native MON back in
 * B's tx (would open a reentrancy / griefing vector; a self-sabotage receive()
 * handler by A could permanently break every subsequent bid on that auction).
 * So A traditionally has to click a "Withdraw refund" button to receive MON.
 *
 * In practice bidders don't come back to click a button; from the user's
 * perspective their MON looks "stuck in escrow". This hook closes the loop
 * **without touching the contract**:
 *
 *   1. On every wallet-ready / sign-in, read all OUTBID bids by the current
 *      Supabase user from the `bids` table. These are the (auction, me) pairs
 *      where a refund MAY be pending on-chain.
 *   2. For each unique auction_id, read `refundOf(auctionId, myAddress)` from
 *      the escrow contract (physical or NFT). This is the ground truth.
 *   3. If the on-chain refund balance is > 0, call `withdrawRefund` from the
 *      user's own Privy embedded wallet (RLS of the contract requires
 *      msg.sender == bidder, so only the user's own signer can authorise the
 *      transfer — this is a critical security property, we don't bypass it).
 *   4. The contract transfers the exact pending amount back to `msg.sender`
 *      (= the exact outbid bidder's embedded wallet). Native MON delta is
 *      refundAmount − gas. A toast reports both.
 *
 * Behaviour:
 *   - Idempotent per session via an in-memory `attempted` set keyed by
 *     `${kind}:${auctionId}`. We never retry the same claim in the same
 *     browser tab; a page refresh re-reads `refundOf` and reclaims only if
 *     a new refund was credited since.
 *   - Serialised: one tx at a time (to avoid noncing issues from parallel
 *     writeContract calls on the same Privy wallet).
 *   - Silent on no-op: if the user has no pending refunds this hook makes
 *     ZERO network/UI noise.
 *   - Error-tolerant: a single claim failure (revert, user rejection, RPC
 *     flake) is logged + a short toast; other pending claims still proceed.
 *   - Scoped to the current user: only reads `bids` rows where
 *     `bidder_id = auth.uid()`, same RLS the rest of the app uses.
 *
 * No contract redeploy, no wallet derivation change, no RLS change.
 */
export function useAutoRefund() {
    const { user, isAuthed } = useAuth();
    const { privyWallet, status: walletStatus, address: myAddress } = useWallet();
    const qc = useQueryClient();
    const attemptedRef = useRef(new Set());

    useEffect(() => {
        if (!isAuthed || !user) return undefined;
        if (walletStatus !== "ready" || !privyWallet || !myAddress) return undefined;
        if (!supabase) return undefined;
        const anyOnchain = isOnchainAvailable() || isNftAvailable();
        if (!anyOnchain) return undefined;

        let cancelled = false;
        (async () => {
            try {
                // Only auctions where this exact user was OUTBID. "WINNING"
                // bids never carry a refund slot. We additionally include the
                // auction's contract_auction_id and type/NFT reference so we
                // can route to the right escrow contract (physical vs NFT).
                const { data: outbidBids, error } = await supabase
                    .from("bids")
                    .select(
                        `id, auction_id,
                         auction:auctions ( id, status, type, contract_auction_id, nft_contract_address, nft_token_id )`
                    )
                    .eq("bidder_id", user.id)
                    .eq("status", "OUTBID")
                    .order("created_at", { ascending: false })
                    .limit(50);
                if (error || cancelled || !outbidBids || outbidBids.length === 0) return;

                // Deduplicate by auction (one refund slot per auction per bidder).
                const byAuction = new Map();
                for (const b of outbidBids) {
                    if (!b.auction) continue;
                    byAuction.set(b.auction.id, b.auction);
                }

                // Check each pending refund on-chain, in order. Only claim
                // when the gas cost is not greater than the refund amount —
                // otherwise the user's balance would visibly decrease, which
                // is the exact UX pain this hook exists to eliminate. We
                // surface those as a soft notice instead of silently signing.
                const gasPrice = await safeGasPrice();
                const estGasWithdraw = 55000n;

                for (const auction of byAuction.values()) {
                    if (cancelled) return;
                    const isNftAuction =
                        auction.type === "NFT" && auction.nft_contract_address && auction.nft_token_id;
                    const key = `${isNftAuction ? "nft" : "phys"}:${auction.id}`;
                    if (attemptedRef.current.has(key)) continue;

                    try {
                        const refund = isNftAuction
                            ? await readNftRefund(auction.nft_contract_address, auction.nft_token_id, myAddress)
                            : await readRefund(auction.id, myAddress);
                        if (!refund || refund === 0n) {
                            attemptedRef.current.add(key); // nothing to claim; don't poll again this session
                            continue;
                        }

                        // Economic sanity: if net would be <= 0 at current
                        // gas, nudge the user rather than silently waste gas.
                        const estGasCost = gasPrice ? gasPrice * estGasWithdraw : 0n;
                        if (estGasCost > 0n && refund <= estGasCost) {
                            attemptedRef.current.add(key); // avoid repeated nudges this session
                            toast("Refund pending — gas higher than refund right now", {
                                description:
                                    `Auction has ${fromWei(refund)} MON waiting for you. Current ` +
                                    `Monad testnet gas (${(Number(gasPrice) / 1e9).toFixed(0)} gwei) would ` +
                                    `cost about ${fromWei(estGasCost)} MON per claim — net ≤ 0. ` +
                                    `Open the auction and claim manually when gas drops, or batch after more outbids.`,
                            });
                            continue;
                        }

                        attemptedRef.current.add(key);
                        toast.loading(`Claiming refund of ${fromWei(refund)} MON…`, { id: key });
                        const { hash, receipt } = isNftAuction
                            ? await withdrawNftRefundOnchain({
                                  wallet: privyWallet,
                                  nftContract: auction.nft_contract_address,
                                  tokenId: auction.nft_token_id,
                              })
                            : await withdrawRefundOnchain({
                                  wallet: privyWallet,
                                  uuid: auction.id,
                              });
                        if (receipt?.status !== "success") throw new Error("withdrawRefund reverted");
                        const gasPaid =
                            BigInt(receipt?.gasUsed || 0n) * BigInt(receipt?.effectiveGasPrice || 0n);
                        const net = BigInt(refund) - gasPaid;
                        const netSign = net >= 0n ? "+" : "";
                        toast.success(`Refunded ${fromWei(refund)} MON`, {
                            id: key,
                            description:
                                `Sent from escrow to ${shorten(myAddress)} · gas ${fromWei(gasPaid)} MON · ` +
                                `net ${netSign}${fromWei(net)} MON`,
                            action: hash
                                ? { label: "Tx", onClick: () => window.open(toExplorerTx(hash), "_blank") }
                                : undefined,
                        });
                        // Invalidate activity caches so the UI reflects the claim
                        qc.invalidateQueries({ queryKey: ["my-activity-bids"] });
                        qc.invalidateQueries({ queryKey: ["auction", auction.id] });
                    } catch (e) {
                        toast.error("Refund claim failed", {
                            id: key,
                            description:
                                (e && (e.shortMessage || e.message)) ||
                                "Try again from the auction room.",
                        });
                        // leave in `attempted` so we don't thrash on a persistent revert
                    }
                }
            } catch {
                /* non-fatal; retried on next sign-in / wallet-ready */
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isAuthed, user, walletStatus, privyWallet, myAddress, qc]);
}

async function safeGasPrice() {
    try {
        return await readClient.getGasPrice();
    } catch {
        return 0n;
    }
}

function shorten(a) {
    if (!a) return "";
    return `${a.slice(0, 6)}…${a.slice(-4)}`;
}
